import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useBreakpoint } from '@app/hooks';
import { CatFileUpload } from '@blocks-react/components/FileUpload';
import { CatIconFileUpload } from '@blocks-react/components/Icons/FileUpload';
import { CatIconAdd } from '@blocks-react/components/Icons/Add';
import { CatButton } from '@blocks-react/components/Button';
import FileUploadSampleFiles from './FileUploadSampleFiles';
import '@blocks-react/components/Icons/DocumentFilled';
import { CREATE_LIST_MODAL_FIELDS, FILE_SIZE_LIMIT } from '../../constants';
import {
  BUTTON_VARIANTS,
  FILE_FORMATS,
  PAGE_BREAKPOINTS
} from '@app/constants/commonConstants';
import ErrorFieldNote from '@app/components/common/Blocks/ErrorFieldNote';

const CreateListFileUpload = ({
  register,
  setValue,
  setError,
  errors,
  watch
}) => {
  const { t } = useTranslation();
  const [isDisabled, setIsDisabled] = useState(false);
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const fileUploadFailed = useSelector(
    state => state?.account?.addProductToSavedListError
  );
  const fileValue = watch(CREATE_LIST_MODAL_FIELDS.FILE_NAME);
  useEffect(() => {
    if (fileUploadFailed) {
      setValue(CREATE_LIST_MODAL_FIELDS.FILE_NAME, []);
      setError(CREATE_LIST_MODAL_FIELDS.FILE_NAME, {
        message: 'FAILED_FILE_CANNOT_BE_UPLOADED'
      });
    }
  }, [fileUploadFailed, setError, setValue]);

  const validate = item => {
    const uploadedFiles = item?.uploadedFiles[0];
    const fileExtension = uploadedFiles?.fileObject[1].name?.split('.').pop();
    const isCsvFile = fileExtension === FILE_FORMATS.csv;
    const isXlsXlsxFile =
      fileExtension === FILE_FORMATS.xls || fileExtension === FILE_FORMATS.xlsx;
    const isValidFile = isCsvFile || isXlsXlsxFile;
    if (uploadedFiles.fileSizeExceeded) {
      return {
        error: 'FAILED_FILE_SIZE_EXCEEDED',
        data: []
      };
    } else if (!isValidFile) {
      return {
        error: 'FAILED_FILE_TYPE_ISNOT_SUPPOERTED',
        data: []
      };
    } else {
      return {
        error: '',
        data: [
          {
            fileId: uploadedFiles?.fieldId,
            status: 'Success',
            statusText: t('FILE_ADDED'),
            fileObject: uploadedFiles?.fileObject,
            action: 'REMOVE',
            actionText: t('REMOVE')
          }
        ]
      };
    }
  };
  const getUploadedFile = (name, values) => {
    setIsDisabled(true);
    const uploadedFile = validate(values);
    setError(CREATE_LIST_MODAL_FIELDS.FILE_NAME, {
      message: uploadedFile.error
    });
    setValue(name, uploadedFile.data);
    setIsDisabled(false);
  };
  const removeUploadedFile = name => {
    setError(CREATE_LIST_MODAL_FIELDS.FILE_NAME, {
      message: ''
    });
    setValue(name, []);
  };
  const uploadButtonText = t('REQUISITIONLIST_BTN_UPLOAD');
  const textConfig = {
    dragandDropText: t('DROP_A_FILE_HERE'),
    uploadingText: t('REQUISITIONLISTITEM_UPLOADING')
  };

  return (
    <div className="cat-u-padding-bottom-xl">
      <CatFileUpload
        {...register(CREATE_LIST_MODAL_FIELDS.FILE_NAME)}
        label={t('ADD_PARTS_TO_YOUR_LIST')}
        fileSizeLimit={FILE_SIZE_LIMIT}
        textConfig={textConfig}
        onBlFileAdd={e => getUploadedFile(e.target.name, e.detail)}
        onBlFileAction={e => removeUploadedFile(e.target.name)}
        uploadFiles={fileValue}
        filesState={fileValue}
        isSmallTable
        variant={isLG ? '' : 'button'}
        disabled={isDisabled}
      >
        <div slot="field-note">
          <FileUploadSampleFiles />
        </div>

        {isLG ? (
          <>
            <CatIconFileUpload />
            <span> {t('DRAG_&_DROP_HERE')} </span>

            <CatButton disabled={isDisabled} variant={BUTTON_VARIANTS.CONTRAST}>
              {uploadButtonText}
            </CatButton>
          </>
        ) : (
          <>
            <CatIconAdd />
            <span>{uploadButtonText}</span>
          </>
        )}
      </CatFileUpload>
      {errors?.[CREATE_LIST_MODAL_FIELDS.FILE_NAME]?.message ? (
        <div className="d-flex my-4">
          <ErrorFieldNote
            error={errors?.[CREATE_LIST_MODAL_FIELDS.FILE_NAME]?.message}
          />
        </div>
      ) : null}
    </div>
  );
};

CreateListFileUpload.propTypes = {
  register: PropTypes.func,
  setValue: PropTypes.func,
  setError: PropTypes.func,
  errors: PropTypes.shape({
    fileName: PropTypes.shape({
      message: PropTypes.string
    })
  }),
  watch: PropTypes.func
};

export default CreateListFileUpload;
